import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Alert, Form } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { getIndicatorOpinionSupport } from "../../../../../rest/indicator";
import {
  handleError,
  isNotBusinessError,
} from "../../../../../utils/handleError";
import Loading from "../../../../../components/general/Loading";
import { currencyFormat } from "../../../../../utils/CurrencyUtils";
import ExportDialog from "../utils/ExportDialog";
import LastUpdate from "../utils/LastUpdate";
import * as XLSX from "xlsx";
import { buildWorkbookOpinionIssuer } from "../sectionsIndicatorsEntities/exporters/exporterIndicatorOpinionIssuer";
import KpiStat from "../utils/KpiStat";
import { useClientPdf } from "../utils/useClientPdf";

export default function IndicatorOpinionIssuer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshVersion, setRefreshVersion] = useState(null);
  const [showExport, setShowExport] = useState(false);

  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("value");
  const [sortDir, setSortDir] = useState("desc");

  const { pdfRef, exportPdf } = useClientPdf({
    marginMm: 10,
    orientation: "portrait",
    hideSelectors: ['[data-hide-on-export="true"]', ".modal", ".toast"],
  });

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const resp = await getIndicatorOpinionSupport();
        const payload = resp?.data || {};
        const list = Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.results)
          ? payload.results
          : [];
        const normalized = list.map((r) => ({
          description: r?.description ?? r?.issuer ?? r?.name ?? "-",
          assigned: Number(r?.assigned ?? r?.assignedCount ?? 0),
          counter: Number(r?.counter ?? r?.casesCount ?? 0),
          paymentCount: Number(r?.paymentCount ?? r?.payments ?? 0),
          value: Number(r?.value ?? r?.valueSum ?? r?.totalValue ?? 0),
          year: r?.year ?? null,
        }));
        setRows(normalized);
        setLastUpdate(payload.lastRefreshedAt ?? payload.lastUpdated ?? null);
        setRefreshVersion(payload.refreshVersion ?? null);
      } catch (err) {
        if (isNotBusinessError(err)) setError(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const round2 = (n) =>
    Math.round((Number(n ?? 0) + Number.EPSILON) * 100) / 100;
  const fmtInt = (n) =>
    typeof n === "number" ? n.toLocaleString("pt-PT") : "0";
  const hasData = rows.length > 0;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      String(r?.description || "-")
        .toLowerCase()
        .includes(q)
    );
  }, [rows, query]);

  const totals = useMemo(
    () =>
      filtered.reduce(
        (acc, r) => {
          acc.assigned += Number(r?.assigned ?? 0);
          acc.counter += Number(r?.counter ?? 0);
          acc.paymentCount += Number(r?.paymentCount ?? 0);
          acc.value += Number(r?.value ?? 0);
          return acc;
        },
        { assigned: 0, counter: 0, paymentCount: 0, value: 0 }
      ),
    [filtered]
  );

  const numEntidades = filtered.length;
  const taxaRealizacaoGlobal =
    totals.assigned > 0 ? (totals.counter / totals.assigned) * 100 : 0;

  const collator = useMemo(
    () => new Intl.Collator("pt-PT", { sensitivity: "base", numeric: true }),
    []
  );

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      if (sortBy === "description") {
        const cmp = collator.compare(
          String(a?.description || ""),
          String(b?.description || "")
        );
        return sortDir === "asc" ? cmp : -cmp;
      }
      const av = Number(a?.[sortBy] ?? 0);
      const bv = Number(b?.[sortBy] ?? 0);
      if (av === bv) {
        const cmp = collator.compare(
          String(a?.description || ""),
          String(b?.description || "")
        );
        return sortDir === "asc" ? cmp : -cmp;
      }
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [filtered, sortBy, sortDir, collator]);

  const maxValor = useMemo(
    () => Math.max(1, ...filtered.map((r) => Number(r?.value ?? 0))),
    [filtered]
  );

  const barWrap = {
    height: 6,
    background: "#eef1f4",
    borderRadius: 999,
    overflow: "hidden",
  };
  const bar = (pct) => ({
    width: `${Math.min(100, Math.max(0, pct))}%`,
    height: "100%",
    background: "#0d6efd",
  });

  const handleExportExcel = async () => {
    try {
      const wb = await buildWorkbookOpinionIssuer();
      XLSX.writeFile(wb, "indicador_emissora_de_parecer.xlsx");
      setTimeout(() => setShowExport(false), 0);
    } catch (e) {
      console.error("[Excel EmissoraParecer] falhou:", e);
      alert("Falha ao gerar Excel: " + (e?.message || e));
    }
  };

  if (loading) return <Loading />;
  if (error) return handleError(error);

  return (
    <div className="container-fluid" ref={pdfRef}>
      <Row className="align-items-center mb-3">
        <Col>
          <h4 className="text-secondary mb-0">
            <FormattedMessage
              id="entity.column.opinion"
              defaultMessage="Entidade Emissora de Parecer"
            />
          </h4>
        </Col>
        <Col className="text-end">
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (!hasData) return;
              setShowExport(true);
            }}
            style={{
              color: "#e5533d",
              textDecoration: "underline",
              opacity: hasData ? 1 : 0.5,
              pointerEvents: hasData ? "auto" : "none",
            }}
            aria-disabled={!hasData}
            data-hide-on-export="true"
          >
            <FormattedMessage
              id="indicator.exportar"
              defaultMessage="Exportar Quadro"
            />
          </a>
        </Col>
      </Row>

      <Row className="g-3 align-items-center mb-2">
        <Col xs={12} md={6}>
          <Form.Control
            value={query}
            placeholder="Pesquisar por Entidade"
            onChange={(e) => setQuery(e.target.value)}
          />
        </Col>
        <Col xs={7} md={3}>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="value">Ordenar por Valor Pago</option>
            <option value="paymentCount">Ordenar por Pagamentos</option>
            <option value="counter">Ordenar por Realizados</option>
            <option value="assigned">Ordenar por Atribuídos</option>
            <option value="description">Ordenar por Entidade</option>
          </Form.Select>
        </Col>
        <Col xs={5} md={3}>
          <Form.Select
            value={sortDir}
            onChange={(e) => setSortDir(e.target.value)}
          >
            <option value="desc">Direção Descendente</option>
            <option value="asc">Direção Ascendente</option>
          </Form.Select>
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat label="N.º de Entidades" value={fmtInt(numEntidades)} />
        </Col>
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat label="Pareceres Atribuídos" value={fmtInt(totals.assigned)} />
        </Col>
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat label="Realizados" value={fmtInt(totals.counter)} />
        </Col>
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat label="Pagos" value={fmtInt(totals.paymentCount)} />
        </Col>
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat label="Valor Pago" value={currencyFormat(totals.value)} />
        </Col>
        <Col xs={12} sm={6} md={4} lg={2}>
          <KpiStat
            label="Taxa de Realização"
            value={`${taxaRealizacaoGlobal.toLocaleString("pt-PT", {
              maximumFractionDigits: 1,
            })}%`}
          />
        </Col>
      </Row>

      {hasData ? (
        <Row className="g-3 row-cols-1 row-cols-md-2 mb-4">
          {sorted.map((r, i) => {
            const a = Number(r?.assigned ?? 0);
            const c = Number(r?.counter ?? 0);
            const p = Number(r?.paymentCount ?? 0);
            const v = Number(r?.value ?? 0);
            const pr = a > 0 ? (c / a) * 100 : 0;
            const pp = c > 0 ? (p / c) * 100 : 0;
            const pctValor = Math.min(100, Math.round((v / maxValor) * 100));
            return (
              <Col key={`${r?.description ?? "row"}-${i}`} className="d-flex">
                <Card className="shadow-sm w-100">
                  <Card.Body className="py-3">
                    <div className="fw-semibold mb-3">
                      {r?.description ?? "-"}
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Atribuídos</span>
                      <strong className="text-body">{fmtInt(a)}</strong>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Realizados</span>
                      <strong className="text-body">{fmtInt(c)}</strong>
                    </div>
                    <div className="d-flex justify-content-between text-muted">
                      <span>Pagos</span>
                      <strong className="text-body">{fmtInt(p)}</strong>
                    </div>
                    <div className="d-flex justify-content-between align-items-center text-muted mt-2">
                      <span>Taxa de realização</span>
                      <strong className="text-body">
                        {pr.toLocaleString("pt-PT", {
                          maximumFractionDigits: 1,
                        })}
                        %
                      </strong>
                    </div>
                    <div style={barWrap} className="mb-2">
                      <div style={bar(pr)} />
                    </div>
                    <div className="d-flex justify-content-between align-items-center text-muted">
                      <span>Taxa de pagamento</span>
                      <strong className="text-body">
                        {pp.toLocaleString("pt-PT", {
                          maximumFractionDigits: 1,
                        })}
                        %
                      </strong>
                    </div>
                    <div style={barWrap} className="mb-2">
                      <div style={bar(pp)} />
                    </div>
                    <div className="d-flex justify-content-between align-items-center text-muted">
                      <span>Valor Pago</span>
                      <strong className="text-body">{currencyFormat(v)}</strong>
                    </div>
                    <div style={barWrap}>
                      <div style={bar(pctValor)} />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
        </Row>
      ) : (
        <Alert variant="light" className="border mb-4">
          <i className="fa-regular fa-circle-info me-2" />
          Sem dados para apresentar.
        </Alert>
      )}

      <LastUpdate iso={lastUpdate} version={refreshVersion} className="mt-3" />

      <ExportDialog
        show={showExport}
        onHide={() => setShowExport(false)}
        options={[
          {
            filename: "indicador_emissora_de_parecer.xlsx",
            onClick: handleExportExcel,
          },
          {
            filename: "indicador_emissora_de_parecer.pdf",
            onClick: () => exportPdf("indicador_emissora_de_parecer.pdf"),
          },
        ]}
      />
    </div>
  );
}